/* Cable assistant: the panel's behaviour. No dependencies. Sends one POST per
   message to the endpoint (data-endpoint) and reads the event stream back:
   status lines while it works, then the answer. The conversation id
   lives in sessionStorage so a page change keeps the thread (the transcript is
   fetched back with GET) and a new tab starts fresh. Page context (product,
   datasheet and other PDF links on this page) goes with every message so the
   assistant can answer about the page.

   Answers carry source markers, [[handle]] for a product page and
   [[handle:datasheet]] for its datasheet, which render as small source links,
   and every product an answer cites is shown as a card under it (photo and
   vendor fetched from the storefront's own /products/<handle>.js). */
(function () {
  "use strict";

  var root = document.querySelector(".ec-assistant");
  if (!root || root.dataset.ecaReady) return;
  root.dataset.ecaReady = "1";

  /* Preview aid: ?launcher=pill|circle|hybrid (or #launcher=...) overrides the
     section's button style for this tab, so one theme shows every look. */
  (function () {
    var m = /[?&#]launcher=(pill|circle|hybrid)\b/.exec(location.search + location.hash);
    var want = m ? m[1] : null;
    try {
      if (want) sessionStorage.setItem("ec-assistant-launcher", want);
      else want = sessionStorage.getItem("ec-assistant-launcher");
    } catch (e) { /* storage blocked: the URL alone still works */ }
    if (!want) return;
    root.className = root.className.replace(/\bec-assistant--(pill|circle|hybrid)\b/g, "").trim() + " ec-assistant--" + want;
  })();

  var endpoint = root.dataset.endpoint;
  var launch = root.querySelector(".eca-launch");
  var panel = root.querySelector(".eca-panel");
  var closeBtn = root.querySelector(".eca-close");
  var salesBtn = root.querySelector(".eca-sales");
  var log = root.querySelector(".eca-log");
  var empty = root.querySelector(".eca-empty");
  var status = root.querySelector(".eca-status");
  var form = root.querySelector(".eca-form");
  var input = root.querySelector(".eca-input");
  var send = root.querySelector(".eca-send");

  var STORE_KEY = "ec-assistant-conv";
  var TIMEOUT_MS = 125000; /* the route may run up to 120 s */
  var SALES = "sales@everwell.com.sg";
  var UNREACHABLE = "Could not reach the assistant. Please email " + SALES + ".";
  var MARKER = /\[\[([a-z0-9][a-z0-9-]*)(?::(datasheet|page))?\]\]/g;
  var busy = false;
  var restored = false;

  function pageContext() {
    var ctx = {
      url: root.dataset.pageUrl || location.href,
      product_handle: root.dataset.productHandle || null,
      product_title: root.dataset.productTitle || null,
      datasheet_url: root.dataset.datasheet || null,
      documents: [],
    };
    if (!ctx.product_handle) return ctx;
    var links = document.querySelectorAll('main a[href$=".pdf"], main a[href*=".pdf?"]');
    for (var i = 0; i < links.length; i++) {
      var href = links[i].href;
      var text = (links[i].textContent || "").toLowerCase();
      if (!/^https:/.test(href)) continue;
      if (!ctx.datasheet_url && (text.indexOf("datasheet") >= 0 || /datasheet/i.test(href))) {
        ctx.datasheet_url = href;
      } else if (ctx.documents.indexOf(href) < 0 && href !== ctx.datasheet_url && ctx.documents.length < 8) {
        ctx.documents.push(href);
      }
    }
    return ctx;
  }

  function getConv() { try { return sessionStorage.getItem(STORE_KEY); } catch (e) { return null; } }
  function setConv(id) { try { if (id) sessionStorage.setItem(STORE_KEY, id); } catch (e) { /* private mode */ } }
  function clearConv() { try { sessionStorage.removeItem(STORE_KEY); } catch (e) { /* ignore */ } }

  /* Phones: the panel fills the visible area, which shrinks when the keyboard
     opens, and the page behind is pinned so Safari cannot scroll it away. */
  var PHONE = window.matchMedia("(max-width: 600px)");
  var vv = window.visualViewport;
  var lockedScrollY = 0;

  function fitViewport() {
    if (panel.hidden || !PHONE.matches) { panel.style.height = ""; panel.style.top = ""; return; }
    if (vv) {
      panel.style.height = Math.round(vv.height) + "px";
      panel.style.top = Math.round(vv.offsetTop) + "px";
    }
    log.scrollTop = log.scrollHeight;
  }
  if (vv) { vv.addEventListener("resize", fitViewport); vv.addEventListener("scroll", fitViewport); }
  window.addEventListener("resize", fitViewport);

  function lockPage() {
    if (!PHONE.matches) return;
    lockedScrollY = window.scrollY || 0;
    document.body.style.top = -lockedScrollY + "px";
    document.documentElement.classList.add("eca-lock");
  }
  function unlockPage() {
    if (!document.documentElement.classList.contains("eca-lock")) return;
    document.documentElement.classList.remove("eca-lock");
    document.body.style.top = "";
    window.scrollTo(0, lockedScrollY);
  }

  function open() {
    panel.classList.remove("eca-panel--closing");
    panel.hidden = false;
    launch.setAttribute("aria-expanded", "true");
    lockPage();
    fitViewport();
    if (!restored) restore();
    if (!PHONE.matches) input.focus();
  }
  function close() {
    if (panel.hidden || panel.classList.contains("eca-panel--closing")) return;
    input.blur();
    launch.setAttribute("aria-expanded", "false");
    var finished = false;
    var finish = function () {
      if (finished) return;
      finished = true;
      panel.removeEventListener("animationend", finish);
      panel.classList.remove("eca-panel--closing");
      panel.hidden = true;
      unlockPage();
      fitViewport();
      launch.focus();
    };
    // The shrink animation runs first; hide the panel when it ends. Without
    // one (reduced motion, old browser) hide it straight away.
    panel.classList.add("eca-panel--closing");
    var animating = typeof panel.getAnimations === "function" && panel.getAnimations().length > 0;
    if (!animating) { finish(); return; }
    panel.addEventListener("animationend", finish);
    setTimeout(finish, 400);
  }

  function setStatus(text) {
    if (text) { status.textContent = text; status.hidden = false; } else { status.textContent = ""; status.hidden = true; }
  }

  function productUrl(handle) { return "/products/" + handle; }

  function linkify(text) {
    var out = document.createDocumentFragment();
    var re = /https?:\/\/[^\s<>)"']+/g;
    var last = 0, m;
    while ((m = re.exec(text))) {
      if (m.index > last) out.appendChild(document.createTextNode(text.slice(last, m.index)));
      var url = m[0].replace(/[.,;:]+$/, "");
      var a = document.createElement("a");
      a.href = url; a.textContent = url; a.target = "_blank"; a.rel = "noopener";
      out.appendChild(a);
      last = m.index + url.length;
    }
    if (last < text.length) out.appendChild(document.createTextNode(text.slice(last)));
    return out;
  }

  /* Text with source markers: each marker becomes a small link to the page
     or the datasheet it names; the rest is plain text with URLs linked. */
  function renderText(text, byHandle) {
    var out = document.createDocumentFragment();
    var last = 0, m;
    MARKER.lastIndex = 0;
    while ((m = MARKER.exec(text))) {
      if (m.index > last) out.appendChild(linkify(text.slice(last, m.index)));
      var handle = m[1], kind = m[2] === "datasheet" ? "datasheet" : "page";
      var c = byHandle[handle];
      var a = document.createElement("a");
      a.className = "eca-src";
      a.href = kind === "datasheet" && c && c.datasheet ? c.datasheet : productUrl(handle);
      a.textContent = kind;
      a.title = kind === "datasheet" ? "Datasheet" : "Product page";
      a.target = "_blank"; a.rel = "noopener";
      /* keep the figure and its marker on one line */
      var sup = document.createElement("sup");
      sup.appendChild(a);
      out.appendChild(sup);
      last = m.index + m[0].length;
    }
    if (last < text.length) out.appendChild(linkify(text.slice(last)));
    return out;
  }

  /* Product cards. Title, vendor and photo come from the storefront itself;
     the datasheet link from the answer's citation when it has one. */
  var productCache = {};
  function productInfo(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch(productUrl(handle) + ".js", { credentials: "same-origin" })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return productCache[handle];
  }
  function imageUrl(src, width) {
    if (!src) return null;
    return src + (src.indexOf("?") >= 0 ? "&" : "?") + "width=" + width;
  }
  function card(c) {
    var el = document.createElement("a");
    el.className = "eca-card";
    el.href = c.url || productUrl(c.handle);
    el.target = "_blank"; el.rel = "noopener";
    var fig = document.createElement("span"); fig.className = "eca-card-img";
    var body = document.createElement("span"); body.className = "eca-card-body";
    var title = document.createElement("span"); title.className = "eca-card-title"; title.textContent = c.title || c.handle;
    var meta = document.createElement("span"); meta.className = "eca-card-meta";
    var links = document.createElement("span"); links.className = "eca-card-links";
    var view = document.createElement("span"); view.className = "eca-card-link"; view.textContent = "View page";
    links.appendChild(view);
    var copyName = document.createElement("button");
    copyName.type = "button"; copyName.className = "eca-card-link eca-card-copy"; copyName.textContent = "Copy name";
    copyName.addEventListener("click", function (e) { e.preventDefault(); e.stopPropagation(); copyText(title.textContent, copyName); });
    links.appendChild(copyName);
    if (c.datasheet) {
      var ds = document.createElement("a");
      ds.className = "eca-card-link"; ds.href = c.datasheet; ds.target = "_blank"; ds.rel = "noopener"; ds.textContent = "Datasheet";
      ds.addEventListener("click", function (e) { e.stopPropagation(); });
      links.appendChild(ds);
    }
    body.appendChild(title); body.appendChild(meta); body.appendChild(links);
    el.appendChild(fig); el.appendChild(body);
    productInfo(c.handle).then(function (p) {
      if (!p) return;
      if (!c.title) title.textContent = p.title;
      meta.textContent = p.vendor || "";
      var src = imageUrl(p.featured_image, 160);
      if (src) { var img = document.createElement("img"); img.src = src; img.alt = ""; img.loading = "lazy"; fig.appendChild(img); }
    });
    return el;
  }

  function stripMarkers(text) { return text.replace(/ ?\[\[([a-z0-9][a-z0-9-]*)(?::(datasheet|page))?\]\]/g, ""); }

  function copyText(text, button, done) {
    var write = navigator.clipboard && navigator.clipboard.writeText ? navigator.clipboard.writeText(text) : Promise.reject();
    write.then(function () { var was = button.textContent; button.textContent = done || "Copied"; setTimeout(function () { button.textContent = was; }, 1500); })
      .catch(function () { window.prompt("Copy:", text); });
  }

  /* Thumbs up or down on one answer, posted to the feedback endpoint next to the chat one. */
  function feedbackRow(text, turn) {
    var row = document.createElement("div");
    row.className = "eca-fb";
    var copy = document.createElement("button");
    copy.type = "button"; copy.className = "eca-fb-btn"; copy.textContent = "Copy answer";
    copy.addEventListener("click", function () { copyText(stripMarkers(text), copy); });
    row.appendChild(copy);
    if (turn && getConv()) {
      var q = document.createElement("span"); q.className = "eca-fb-q"; q.textContent = "Helpful?";
      row.appendChild(q);
      ["Yes", "No"].forEach(function (label, i) {
        var b = document.createElement("button");
        b.type = "button"; b.className = "eca-fb-btn"; b.textContent = label;
        b.addEventListener("click", function () {
          var rating = i === 0 ? 1 : -1;
          fetch(endpoint.replace(/\/chat$/, "/feedback"), {
            method: "POST", headers: { "content-type": "application/json" },
            body: JSON.stringify({ conversation_id: getConv(), turn: turn, rating: rating }),
          }).catch(function () { /* feedback is best effort */ });
          q.textContent = rating > 0 ? "Thanks." : "Thanks, we will look at this one.";
          row.querySelectorAll(".eca-fb-btn").forEach(function (x) { if (x !== copy) x.remove(); });
        });
        row.appendChild(b);
      });
    }
    return row;
  }

  function addMessage(kind, text, cited, extra) {
    if (empty) { empty.remove(); empty = null; }
    extra = extra || {};
    var p = document.createElement("div");
    p.className = "eca-msg eca-msg--" + kind;
    var byHandle = {};
    (cited || []).forEach(function (c) { if (c && c.handle) byHandle[c.handle] = c; });
    /* A restored line has no citation list: derive the cards from its markers. */
    var m; MARKER.lastIndex = 0;
    while ((m = MARKER.exec(text))) { if (!byHandle[m[1]]) byHandle[m[1]] = { handle: m[1], title: "", url: productUrl(m[1]), datasheet: null }; }
    p.appendChild(kind === "assistant" ? renderText(text, byHandle) : linkify(text));
    var handles = Object.keys(byHandle);
    if (kind === "assistant" && handles.length) {
      var cards = document.createElement("div");
      cards.className = "eca-cards";
      handles.slice(0, 4).forEach(function (h) { cards.appendChild(card(byHandle[h])); });
      p.appendChild(cards);
    }
    if (kind === "assistant") p.appendChild(feedbackRow(text, extra.turn));
    /* Options the assistant offered: tap one and it is sent as the reply. */
    if (kind === "assistant" && extra.chips && extra.chips.length) {
      var chips = document.createElement("div");
      chips.className = "eca-chips eca-chips--follow";
      extra.chips.forEach(function (label) {
        var b = document.createElement("button");
        b.type = "button"; b.className = "eca-chip"; b.textContent = label; b.dataset.message = label;
        chips.appendChild(b);
      });
      p.appendChild(chips);
    }
    log.appendChild(p);
    log.scrollTop = log.scrollHeight;
  }

  /* After a page change the id survives but the panel is empty: draw the
     thread again so the customer sees what the assistant is referring to. */
  function restore() {
    restored = true;
    var id = getConv();
    if (!id) return;
    fetch(endpoint + "?conversation_id=" + encodeURIComponent(id), { method: "GET" })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (!data || !data.lines) { clearConv(); return; }
        if (!data.lines.length) { clearConv(); return; }
        data.lines.forEach(function (l) { addMessage(l.role === "customer" ? "customer" : "assistant", l.text); });
        if (data.enquiry_sent) addMessage("assistant", "Your quote request is with " + SALES + ".");
      })
      .catch(function () { /* start fresh silently */ });
  }

  function parseEvents(buffer, onEvent) {
    var parts = buffer.split("\n\n");
    for (var i = 0; i < parts.length - 1; i++) {
      var lines = parts[i].split("\n");
      var data = "";
      for (var j = 0; j < lines.length; j++) {
        if (lines[j].indexOf("data:") === 0) data += lines[j].slice(5).trim();
      }
      if (!data) continue;
      try { onEvent(JSON.parse(data)); } catch (e) { /* skip a bad frame */ }
    }
    return parts[parts.length - 1];
  }

  function setBusy(b) {
    busy = b;
    input.disabled = b;
    send.disabled = b;
  }

  function sendMessage(text) {
    if (busy) return;
    log.querySelectorAll(".eca-chips--follow").forEach(function (c) { c.remove(); });
    addMessage("customer", text);
    setBusy(true);
    setStatus("Sending…");

    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, TIMEOUT_MS);
    var body = { conversation_id: getConv(), message: text, context: pageContext(), features: ["sources", "chips"] };
    var gotFinal = false;

    function handle(ev) {
      if (ev.type === "status") setStatus(ev.text + "…");
      else if (ev.type === "message") { gotFinal = true; setStatus(""); addMessage("assistant", ev.text, ev.cited, { chips: ev.chips, turn: ev.turn }); }
      else if (ev.type === "enquiry_sent") addMessage("assistant", "Sent. " + SALES + " will reply by email.");
      else if (ev.type === "error") { gotFinal = true; setStatus(""); addMessage("error", ev.text); }
    }

    fetch(endpoint, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
    }).then(function (res) {
      var id = res.headers.get("X-Conversation-Id");
      if (id) setConv(id);
      var type = res.headers.get("content-type") || "";
      if (!res.body || type.indexOf("text/event-stream") < 0) throw new Error("not a stream: " + res.status);
      var reader = res.body.getReader();
      var decoder = new TextDecoder();
      var buffer = "";
      function pump() {
        return reader.read().then(function (r) {
          if (r.done) { buffer = parseEvents(buffer + "\n\n", handle); return; }
          buffer += decoder.decode(r.value, { stream: true });
          buffer = parseEvents(buffer, handle);
          return pump();
        });
      }
      return pump();
    }).then(function () {
      /* A stream that ended without an answer (a cut-off at the time limit) is an error, not silence. */
      if (!gotFinal) { setStatus(""); addMessage("error", UNREACHABLE); }
    }).catch(function (err) {
      setStatus("");
      addMessage("error", err && err.name === "AbortError"
        ? "Taking too long. Please email " + SALES + "."
        : UNREACHABLE);
    }).then(function () {
      clearTimeout(timer);
      setBusy(false);
      input.focus();
    });
  }

  launch.addEventListener("click", open);
  closeBtn.addEventListener("click", close);
  panel.addEventListener("keydown", function (e) { if (e.key === "Escape") close(); });

  /* Suggested questions and the header's sales link send a prepared message. */
  panel.addEventListener("click", function (e) {
    var b = e.target && e.target.closest ? e.target.closest(".eca-chip, .eca-sales") : null;
    if (!b || !b.dataset.message) return;
    e.preventDefault();
    sendMessage(b.dataset.message);
  });
  if (salesBtn && !salesBtn.dataset.message) salesBtn.dataset.message = "I would like a quote from your sales team.";

  /* Any link to #cable-finder opens the panel, so a page can say
     "or ask Cable Finder" in its own text without more code. */
  document.addEventListener("click", function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href="#cable-finder"], [data-cable-finder]') : null;
    if (!a) return;
    e.preventDefault();
    open();
  });
  if (location.hash === "#cable-finder") open();

  /* While the theme's cookie panel is showing (bottom left, nearly full width
     on a phone) the button would sit under it, so it steps aside. */
  var cookieBar = document.querySelector('[class^="ck-bar-"]');
  if (cookieBar) {
    var syncCookie = function () { launch.style.visibility = cookieBar.hasAttribute("data-visible") ? "hidden" : ""; };
    syncCookie();
    new MutationObserver(syncCookie).observe(cookieBar, { attributes: true, attributeFilter: ["data-visible"] });
  }

  form.addEventListener("submit", function (e) {
    e.preventDefault();
    var text = input.value.trim();
    if (!text) return;
    input.value = "";
    input.style.height = "";
    sendMessage(text);
  });

  input.addEventListener("keydown", function (e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); form.requestSubmit(); }
  });
  input.addEventListener("input", function () {
    input.style.height = "";
    input.style.height = Math.min(input.scrollHeight, 120) + "px";
  });
})();
